import React, { useState, useEffect } from "react";
import { Copy, Printer, AlertTriangle, Trophy, Mail, Loader2, ChevronDown, ChevronsUpDown, Pencil } from "lucide-react";
import { SectionHeading, StatTile, StepInstructions } from "@/components/wizard/ui";
import { SportIcon } from "@/components/wizard/icons";
import PrintHeader from "@/components/PrintHeader";
import { DEMAND_COLOR, SPORTS, INTENSITY } from "@/lib/planModel";
import FuellingDay from "@/components/wizard/FuellingDay";
import { WeeklyCharts } from "@/components/wizard/WeeklyCharts";
import PlanInsights from "@/components/wizard/PlanInsights";
import PdfCover from "@/components/PdfCover";
import CoachBrief from "@/components/wizard/CoachBrief";
import WeeklyScoreboard from "@/components/wizard/WeeklyScoreboard";
import WeeklyTips from "@/components/wizard/WeeklyTips";
import api from "@/lib/api";
import { litresLabel } from "@/lib/units";
import { fmtDuration, fmtDistanceFromMi } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SPORT_LABEL = Object.fromEntries(SPORTS.map((s) => [s.value, s.label]));
const INTENSITY_LABEL = Object.fromEntries(INTENSITY.map((i) => [i.value, i.label]));

function Section({ title, defaultOpen = false, testId, children, bulk }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (bulk) setOpen(bulk.open); }, [bulk]);
  return (
    <div className="mb-4 border border-white/10" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}
        className="no-print w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.03] transition-colors">
        <span className="text-xs tracking-[0.25em] uppercase text-[#E2E30A]">{title}</span>
        <ChevronDown className={`h-4 w-4 text-zinc-300 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={open ? "px-4 pb-4" : "hidden print:block px-4 pb-4"}>{children}</div>
    </div>
  );
}

function weekRange(weekStart) {
  if (!weekStart) return "";
  const start = new Date(`${weekStart}T00:00:00`);
  if (isNaN(start.getTime())) return "";
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const o1 = { day: "numeric", month: "short" };
  const o2 = { day: "numeric", month: "short", year: "numeric" };
  return `${start.toLocaleDateString("en-GB", o1)} – ${end.toLocaleDateString("en-GB", o2)}`;
}

function dayDate(weekStart, index) {
  if (!weekStart) return "";
  const d = new Date(`${weekStart}T00:00:00`);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + index);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function nameFromDate(weekStart) {
  if (!weekStart) return "";
  const d = new Date(`${weekStart}T00:00:00`);
  if (isNaN(d.getTime())) return "";
  return `w/c ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
}

export default function Step8Summary({ plan, setTop, setStep, isLite, setPlan }) {
  const { generated, profile, goal, unitSystem, name, weekStart, selections, dietary, raceWeek, trainingWeek } = plan;
  const targets = generated?.targets;
  const dayPathways = generated?.dayPathways;
  const fuelBy = {};
  (generated?.fuelling || []).forEach((f) => { fuelBy[f.index] = f; });
  const [copying, setCopying] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [bulk, setBulk] = useState(null);

  const toggleAll = () => { const nv = !allOpen; setAllOpen(nv); setBulk({ open: nv, t: Date.now() }); };
  const printAll = () => {
    // Expand every section first so Recharts (ResponsiveContainer) can measure and
    // render — collapsed/hidden charts otherwise print blank. Wait for the resize.
    setAllOpen(true);
    setBulk({ open: true, t: Date.now() });
    setTimeout(() => window.print(), 500);
  };

  const sessionsFor = (idx) => (trainingWeek?.days?.[idx]?.sessions) || [];

  const buildDayText = (dp) => {
    const sel = selections[dp.index] || "A";
    const path = dp.pathways[sel];
    const daily = targets.daily?.[dp.index];
    const fuel = fuelBy[dp.index];
    const sess = sessionsFor(dp.index);
    const dateStr = dayDate(weekStart, dp.index);
    const fl = (ml) => litresLabel(ml);
    let t = `${dp.day.toUpperCase()}${dateStr ? " · " + dateStr : ""} — ${dp.isRace ? "RACE" : (dp.demand || "").replace("_", " ").toUpperCase()} DAY\n`;
    t += `Targets: ${dp.target.energy} kcal | ${dp.target.protein}g P · ${dp.target.carb}g C · ${dp.target.fat}g F\n`;
    if (daily?.hydrationMl) t += `Hydration: ${fl(daily.hydrationMl)} baseline\n`;
    if (sess.length) {
      t += `Training: ${sess.map((s) => `${SPORT_LABEL[s.sport] || s.sport} ${s.duration}min ${INTENSITY_LABEL[s.intensity] || s.intensity}${s.timing ? ` (${s.timing})` : ""}${s.heat ? " [heat]" : ""}`).join("; ")}\n`;
    } else {
      t += `Training: Rest day\n`;
    }
    (fuel?.sessions || []).forEach((se) => {
      t += `Fuelling — ${SPORT_LABEL[se.sport] || se.sport} ${se.duration}min:\n`;
      t += `  Pre: ${se.preG}g carbs${se.preFluidMl != null ? ` + ${fl(se.preFluidMl)} fluid` : ""}\n`;
      t += `  During: ${se.duringPerHr ? `${se.duringPerHr}g/h carbs` : "no carbs needed"}${se.duringFluidPerHr != null ? ` + ${fl(se.duringFluidPerHr)}/h` : ""}${se.sodiumPerHr ? ` + ~${se.sodiumPerHr}mg sodium/h` : ""}\n`;
      t += `  Post: ${se.postG}g carbs${se.postFluidMl != null ? ` + ${fl(se.postFluidMl)} fluid` : ""}\n`;
    });
    if (dp.fuelPlan) {
      t += `Big-day top-up (~${dp.shortfallKcal} kcal short of meals): ${dp.fuelPlan.summary}\n`;
      t += `  = +${dp.fuelPlan.kcal} kcal / ${dp.fuelPlan.carb}g carbs from in-session fuel\n`;
    }
    if (!isLite) {
      t += `Meals (Pathway ${sel}):\n`;
      (path?.meals || []).forEach((m) => { t += `  - ${m.name} — ${Math.round(m.kcal)} kcal (${Math.round(m.protein)}P ${Math.round(m.carb)}C ${Math.round(m.fat)}F)\n`; });
    }
    return t;
  };

  const buildText = () => {
    let out = `PERFORMANCE NUTRITION — WEEKLY PLAN — ${name}\n`;
    out += `Athlete: ${profile.name || "—"} · Goal: ${goal}${weekRange(weekStart) ? ` · Week of ${weekRange(weekStart)}` : ""}\n`;
    out += `Weekly demand: ${targets?.weeklyDemand} · Avg ${targets?.weekAverage.energy} kcal/day\n\n`;
    (dayPathways || []).forEach((dp) => { out += buildDayText(dp) + "\n"; });
    return out;
  };

  const copyDay = (dp) => {
    navigator.clipboard.writeText(buildDayText(dp)).catch(() => {});
    toast.success(`${dp.day} copied — paste into TrainingPeaks`);
  };

  const copyPlan = () => {
    setCopying(true);
    navigator.clipboard.writeText(buildText()).catch(() => {});
    toast.success("Full plan copied to clipboard");
    setTimeout(() => setCopying(false), 800);
  };

  const buildHtml = () => {
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const daysHtml = (dayPathways || []).map((dp) => {
      const sel = selections[dp.index] || "A";
      const path = dp.pathways[sel];
      const sess = sessionsFor(dp.index);
      const fuel = fuelBy[dp.index];
      const daily = targets.daily?.[dp.index];
      const dstr = dayDate(weekStart, dp.index);
      const training = sess.length
        ? sess.map((s) => `${esc(SPORT_LABEL[s.sport] || s.sport)} ${s.duration}min ${esc(INTENSITY_LABEL[s.intensity] || s.intensity)}`).join("; ")
        : "Rest day";
      const fuelRows = (fuel?.sessions || []).map((se) =>
        `<div style="font-size:13px;color:#333;margin:3px 0"><b>${esc(SPORT_LABEL[se.sport] || se.sport)} ${se.duration}min</b> — Pre ${se.preG}g / ${litresLabel(se.preFluidMl)} · During ${se.duringPerHr ? se.duringPerHr + "g/h" : "—"} / ${litresLabel(se.duringFluidPerHr)}/h${se.sodiumPerHr ? ` · ~${se.sodiumPerHr}mg Na/h` : ""} · Post ${se.postG}g / ${litresLabel(se.postFluidMl)}</div>`).join("");
      const fuelPlanHtml = dp.fuelPlan
        ? `<div style="font-size:13px;color:#8a5a00;background:#fff6e6;border-left:3px solid #FF9500;padding:6px 8px;margin-top:6px"><b>Big-day top-up (+${dp.fuelPlan.kcal} kcal · ${dp.fuelPlan.carb}g C):</b> ${esc(dp.fuelPlan.summary)}</div>`
        : "";
      const meals = isLite ? "" : (path?.meals || []).map((m) =>
        `<li style="font-size:13px;color:#333">${esc(m.name)} — ${Math.round(m.kcal)} kcal (${Math.round(m.protein)}P ${Math.round(m.carb)}C ${Math.round(m.fat)}F)</li>`).join("");
      const mealsHtml = isLite ? "" : `<div style="font-size:13px;color:#333;margin-top:6px"><b>Meals (Pathway ${sel}):</b></div>
        <ul style="margin:4px 0 0;padding-left:18px">${meals}</ul>`;
      return `<table width="100%" style="border-collapse:collapse;margin:0 0 14px;border-left:4px solid ${DEMAND_COLOR[dp.demand] || "#004AAD"}"><tr><td style="padding:10px 14px;background:#f6f7f9">
        <div style="font-weight:800;text-transform:uppercase;font-size:15px;color:#111">${esc(dp.day)}${dstr ? ` · ${esc(dstr)}` : ""} <span style="font-size:12px;color:#666">(${dp.isRace ? "Race" : esc(dp.demand)})</span></div>
        <div style="font-size:13px;color:#004AAD;margin:2px 0 6px">${dp.target.energy} kcal · ${dp.target.protein}g P · ${dp.target.carb}g C · ${dp.target.fat}g F${daily?.hydrationMl ? ` · Hydration ${litresLabel(daily.hydrationMl)}` : ""}</div>
        <div style="font-size:13px;color:#333"><b>Training:</b> ${training}</div>
        ${fuelRows}
        ${fuelPlanHtml}
        ${mealsHtml}
      </td></tr></table>`;
    }).join("");
    return `<div style="font-family:Arial,Helvetica,sans-serif;max-width:640px;margin:0 auto;color:#111">
      <table width="100%" style="border-collapse:collapse;border-bottom:3px solid #004AAD;margin-bottom:16px"><tr><td style="padding:8px 0">
        <div style="font-weight:900;text-transform:uppercase;font-size:20px;color:#004AAD">Performance Nutrition</div>
        <div style="font-size:12px;color:#555;text-transform:uppercase;letter-spacing:2px">Weekly Nutrition Plan</div>
      </td></tr></table>
      <div style="font-size:14px;margin-bottom:14px"><b>${esc(profile.name || name)}</b>${weekRange(weekStart) ? ` · Week of ${esc(weekRange(weekStart))}` : ""} · Goal: ${esc(goal)}<br/>
      Avg ${targets.weekAverage.energy} kcal/day · Weekly load: ${esc(targets.weeklyDemand)}</div>
      ${daysHtml}
      <p style="font-size:11px;color:#888;margin-top:18px">Generated by Performance Nutrition · Powered by SPC · smartperformancecoaching.co.uk</p>
    </div>`;
  };

  const emailPlan = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/email/plan", { subject: `Your Performance Nutrition Weekly Plan — ${name}`, htmlContent: buildHtml() });
      toast.success(`Plan emailed to ${data.recipient}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" && d.length < 200 ? d : "Could not email the plan. Please try again.");
    } finally {
      setEmailing(false);
    }
  };

  if (!targets) {
    return (
      <div>
        <SectionHeading eyebrow="Step 09" title="Weekly Summary" />
        <p className="text-zinc-200">Generate your plan in Step 6 to see the full summary.</p>
      </div>
    );
  }

  // Meal-planning toggle set on Step 2. Only `true` (explicit YES) shows
  // meal/pathway/shopping UI in this Summary; anything else falls back to
  // the targets-only view.
  const wantsMealPlanning = plan.mealPlanning === true && !!dayPathways;

  return (
    <div className="print-area">
      <PdfCover athlete={profile.name} planName={name} weekLabel={weekRange(weekStart)} goal={goal}
        weekAverage={targets.weekAverage} weeklyDemand={targets.weeklyDemand} />
      <PrintHeader title="Weekly Nutrition Plan" subtitle={`${name}${profile.name ? " · " + profile.name : ""} · Goal: ${goal}`} />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 no-print">
        <SectionHeading eyebrow="Step 09" title="Weekly Summary & Export"
          desc="Your complete plan. Name it, copy it, or print to PDF." />
        <div className="flex gap-2">
          {!isLite && wantsMealPlanning && (
          <Button data-testid="edit-meals-btn" onClick={() => setStep(6)} variant="outline"
            className="rounded-none border-[#E2E30A]/50 bg-[#E2E30A]/10 hover:bg-[#E2E30A]/25 uppercase text-xs tracking-wider">
            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit meals
          </Button>
          )}
          <Button data-testid="copy-plan-btn" onClick={copyPlan} variant="outline"
            className="rounded-none border-white/10 bg-transparent hover:bg-white/5 text-white uppercase text-xs tracking-wider">
            <Copy className="h-3.5 w-3.5 mr-2" /> {copying ? "Copied" : "Copy"}
          </Button>
          <Button data-testid="email-plan-btn" onClick={emailPlan} disabled={emailing} variant="outline"
            className="rounded-none border-[#004AAD]/50 bg-[#004AAD]/10 hover:bg-[#004AAD]/25 uppercase text-xs tracking-wider">
            {emailing ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-2" />} {emailing ? "Sending" : "Email"}
          </Button>
          <Button data-testid="print-plan-btn" onClick={printAll}
            className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase text-xs tracking-wider transition-colors">
            <Printer className="h-3.5 w-3.5 mr-2" /> Print / PDF
          </Button>
        </div>
      </div>

      <StepInstructions>
        Review your week (the plan name comes from the week starting date set in Step 1). Then <span className="text-[#E2E30A]">Copy</span>, <span className="text-[#E2E30A]">Email</span> or <span className="text-[#E2E30A]">Print / PDF</span> it. Don't miss the <span className="text-[#E2E30A]">Coach's Notes</span>.
      </StepInstructions>

      <div className="mb-6 max-w-md" data-testid="plan-name-display">
        <p className="text-sm text-white">
          Plan: <span className="font-display uppercase text-[#E2E30A]">{name || "—"}</span>
          {weekRange(weekStart) && <span className="text-zinc-200 text-xs ml-2">Week of {weekRange(weekStart)}</span>}
        </p>
      </div>

      {!wantsMealPlanning && (
        <div className="mb-6 border border-[#E2E30A]/40 bg-[#E2E30A]/[0.04] px-4 py-3" data-testid="targets-only-banner">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#E2E30A] font-semibold mb-1">Targets-only plan</p>
          <p className="text-sm text-zinc-200 leading-snug">
            Meal planning is off — we've built your weekly numbers (kcal, macros, hydration) and skipped
            the meal pathways &amp; shopping list. Flip it back on in <b>Step 2</b> anytime and regenerate
            to add meals.
          </p>
        </div>
      )}

      <div className="flex items-center justify-end mb-3 no-print">
        <Button data-testid="toggle-all-sections-btn" onClick={toggleAll} variant="outline"
          className="rounded-none border-white/10 bg-transparent hover:bg-white/5 text-white uppercase text-xs tracking-wider">
          <ChevronsUpDown className="h-3.5 w-3.5 mr-2" /> {allOpen ? "Collapse all" : "Expand all"}
        </Button>
      </div>

      <Section title="Your Week, In Brief" testId="section-brief" defaultOpen bulk={bulk}>
        <CoachBrief targets={targets} trainingWeek={trainingWeek} raceWeek={raceWeek} goal={goal} />
      </Section>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatTile testId="summary-avg-energy" label="Avg Energy" value={targets.weekAverage.energy} unit="kcal" accent="#4C9AFF" />
        <StatTile testId="summary-avg-protein" label="Avg Protein" value={targets.weekAverage.protein} unit="g" />
        <StatTile testId="summary-avg-carb" label="Avg Carbs" value={targets.weekAverage.carb} unit="g" accent="#34C759" />
        <StatTile testId="summary-demand" label="Weekly Demand" value={targets.weeklyDemand} accent="#FF9500" dense />
      </div>

      <Section title="Weekly Scoreboard" testId="section-scoreboard" bulk={bulk}>
        <WeeklyScoreboard targets={targets} trainingWeek={trainingWeek} raceWeek={raceWeek} />
      </Section>

      {raceWeek.enabled && (
        <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4 mb-8 flex items-start gap-3">
          <Trophy className="h-5 w-5 text-[#FF3B30] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-white font-semibold">Race Week: {raceWeek.event || "Event"}</p>
            <p className="text-xs text-zinc-200 mt-1">{raceWeek.carbsPerHour}g carbs/hr intra-race. {raceWeek.strategy}</p>
          </div>
        </div>
      )}

      <Section title="Week at a Glance" testId="section-glance" bulk={bulk}>
        <div data-testid="summary-charts">
          <WeeklyCharts daily={targets.daily} weekAverage={targets.weekAverage} />
        </div>
      </Section>

      <Section title="Coach's Notes" testId="section-notes" bulk={bulk}>
        <PlanInsights targets={targets} raceWeek={raceWeek} profile={profile} />
      </Section>

      <Section title="Pro Tips for the Week" testId="section-tips" bulk={bulk}>
        <WeeklyTips targets={targets} raceWeek={raceWeek} profile={profile} goal={goal} />
      </Section>

      {!wantsMealPlanning && targets?.daily && (
        <Section title="Daily Targets" testId="section-daily-targets" bulk={bulk}>
          <p className="text-[11px] text-zinc-200 mb-3">Guidelines only — meal planning is off, so no pathways/shopping. Follow the numbers.</p>
          <div className="space-y-2">
            {targets.daily.map((d) => {
              const sess = sessionsFor(d.index);
              return (
                <div key={d.index} data-testid={`summary-day-targets-${d.index}`}
                  className="border border-white/10 bg-[#141414] p-3 flex flex-wrap items-center justify-between gap-3"
                  style={{ borderLeftColor: DEMAND_COLOR[d.demand], borderLeftWidth: 3 }}>
                  <span className="font-display font-bold text-sm uppercase text-white min-w-[80px]">{d.day}</span>
                  <span className="text-[11px] uppercase tracking-wider text-zinc-300 flex-1 min-w-0 truncate">
                    {sess.length === 0 ? "Rest" : sess.map((s) => `${SPORT_LABEL[s.sport] || s.sport} ${fmtDuration(s.duration)}`).join(" · ")}
                  </span>
                  <span className="text-sm font-mono font-semibold text-zinc-200 whitespace-nowrap">
                    {d.energy}kcal · <span className="text-[#4C9AFF] font-bold">{d.protein}P</span> <span className="text-[#34C759] font-bold">{d.carb}C</span> <span className="text-[#FF9500] font-bold">{d.fat}F</span>
                  </span>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {wantsMealPlanning && (
      <Section title="Daily Breakdown" testId="section-daily" bulk={bulk}>
      <div className="flex items-center justify-between mb-4 no-print">
        <span className="text-[11px] text-zinc-200">Tap Copy on any day to paste into a tracker</span>
      </div>

      <div className="space-y-3 mb-8">
        {(dayPathways || []).map((dp) => {
          const sel = selections[dp.index] || "A";
          const path = dp.pathways[sel];
          return (
            <div key={dp.index} data-testid={`summary-day-${dp.index}`} className="border border-white/10 bg-[#141414] p-4 print-day"
              style={{ borderLeftColor: DEMAND_COLOR[dp.demand], borderLeftWidth: 3 }}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="font-display font-bold text-lg uppercase text-white">
                  {dp.day}
                  {dayDate(weekStart, dp.index) && <span className="text-[11px] font-sans font-normal normal-case text-zinc-200 ml-2">{dayDate(weekStart, dp.index)}</span>}
                  <span className="text-xs text-zinc-200 ml-2">Pathway {sel}</span>
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono font-semibold text-zinc-200">{dp.target.energy}kcal · <span className="text-[#4C9AFF] font-bold">{dp.target.protein}P</span> <span className="text-[#34C759] font-bold">{dp.target.carb}C</span> <span className="text-[#FF9500] font-bold">{dp.target.fat}F</span></span>
                  <button data-testid={`copy-day-${dp.index}`} onClick={() => copyDay(dp)} title="Copy this day — ready for TrainingPeaks"
                    className="no-print inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-200 hover:text-[#E2E30A] border border-white/10 hover:border-[#E2E30A]/50 px-2 py-1 transition-colors">
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
              </div>
              {(() => {
                const sess = sessionsFor(dp.index);
                return (
                  <div className="flex flex-wrap gap-2 mb-2" data-testid={`summary-training-${dp.index}`}>
                    {sess.length === 0 ? (
                      <span className="text-[11px] uppercase tracking-wider text-zinc-200">Rest day</span>
                    ) : sess.map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider border border-white/25 px-2 py-1 print-accent" style={{ color: "#F5F1E8" }}>
                        <SportIcon sport={s.sport} className="h-3 w-3 text-[#E2E30A]" />
                        {SPORT_LABEL[s.sport] || s.sport} · {fmtDuration(s.duration)}{s.tpDistanceMi ? ` · ${fmtDistanceFromMi(s.tpDistanceMi, unitSystem)}` : ""} · {INTENSITY_LABEL[s.intensity] || s.intensity}{s.heat ? " · heat" : ""}
                      </span>
                    ))}
                  </div>
                );
              })()}
              {!isLite && wantsMealPlanning && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-200">
                {path?.meals.map((m, i) => <span key={i}>• {m.name}</span>)}
              </div>
              )}
              {dp.fuelPlan && (
                <div data-testid={`summary-fuelplan-${dp.index}`}
                  className="mt-2 border-l-2 border-[#FF9500] bg-[#FF9500]/10 px-3 py-2 text-xs text-[#FFB454] print-accent">
                  <span className="uppercase tracking-wider text-[10px] text-[#FF9500]">Big-day fuel top-up · +{dp.fuelPlan.kcal} kcal · {dp.fuelPlan.carb}g carbs</span>
                  <div className="text-zinc-200 mt-1">{dp.fuelPlan.summary}</div>
                </div>
              )}
              {wantsMealPlanning && (
                <FuellingDay day={fuelBy[dp.index]} hydrationMl={targets?.daily?.[dp.index]?.hydrationMl} unitSystem={unitSystem} />
              )}
            </div>
          );
        })}
      </div>
      </Section>
      )}

      <div className="border border-white/10 bg-[#141414] p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-[#FF9500] shrink-0 mt-0.5" />
        <p className="text-xs text-zinc-200 leading-relaxed">
          <span className="text-zinc-200 uppercase tracking-wider">Disclaimer:</span> This plan is generated for healthy adults for general performance-nutrition guidance only. It is not medical or dietetic advice. Consult a qualified professional before making significant dietary changes, especially with medical conditions, pregnancy, or eating disorders.
        </p>
      </div>
    </div>
  );
}
